// Pull-to-refresh for the GRIT PWA. The window/document scrolls and the router
// swaps #app per screen, so this lives ONCE on document (added from main.js).
// Pull down from the very top past the threshold to reload. A full reload is
// safe: the logger persists in-progress state and the SW is network-first.
// A manual "Refresh data" item in the account menu is the guaranteed fallback.

const DAMP = 0.5;              // finger travel is halved for a natural drag feel
const MAX_PULL = 96;          // cap the pill travel
const DAMPED_TRIGGER = 36;    // damped px needed to fully arm (ring full at ~72px raw)
const ARM_THRESHOLD = 4;      // px before we commit to a direction
const RECENT_SCROLL_MS = 350; // ignore pulls right after momentum scrolling (no phantom refresh)

let pill, ring;
let startY = 0, startX = 0;
let armed = false, tracking = false, refreshing = false;
let lastScroll = 0;

const now = () => Date.now();
function atTop() { return (window.scrollY || document.documentElement.scrollTop || 0) <= 0; }

// A full-screen sheet/overlay/onboarding is open: never refresh underneath it.
export function overlayOpen() {
  if (document.querySelector('body > .fixed.inset-0')) return true;
  for (const node of document.body.children) {
    if (node === pill) continue;
    if (!node.getClientRects().length) continue; // hidden
    const st = getComputedStyle(node);
    if (st.position === 'fixed' && (parseInt(st.zIndex, 10) || 0) >= 100) return true;
  }
  return false;
}

// Don't hijack the rest timer, form fields, or a nested scroller that isn't at its own top.
function inBlockedTarget(t) {
  if (!t || !t.closest) return false;
  if (t.closest('#grit-rest-timer') || t.closest('input,textarea,select,[contenteditable],[data-no-ptr]')) return true;
  let n = t;
  while (n && n !== document.body) {
    const st = getComputedStyle(n);
    if (/(auto|scroll)/.test(st.overflowY) && n.scrollTop > 0) return true;
    n = n.parentElement;
  }
  return false;
}

function buildPill() {
  if (pill) return;
  pill = document.createElement('div');
  pill.id = 'grit-ptr';
  pill.style.cssText = 'position:fixed;left:50%;top:calc(env(safe-area-inset-top,0px) + 8px);width:36px;height:36px;border-radius:9999px;background:#161619;box-shadow:0 10px 22px -6px rgb(var(--brand) / .5);z-index:55;display:flex;align-items:center;justify-content:center;transform:translate(-50%,-160%);opacity:0;pointer-events:none;transition:transform .2s ease,opacity .2s ease';
  ring = document.createElement('div');
  ring.style.cssText = 'width:20px;height:20px;border-radius:9999px;background:conic-gradient(rgb(var(--brand)) var(--deg,0deg), rgba(255,255,255,.18) 0);-webkit-mask:radial-gradient(circle 6px at center, transparent 98%, #000 100%);mask:radial-gradient(circle 6px at center, transparent 98%, #000 100%)';
  pill.appendChild(ring);
  document.body.appendChild(pill);
  const style = document.createElement('style');
  style.textContent = '@keyframes grit-ptr-spin{to{transform:rotate(360deg)}}#grit-ptr.is-spin>div{background:conic-gradient(rgb(var(--brand)) 300deg, rgba(255,255,255,.18) 0);animation:grit-ptr-spin .7s linear infinite}@media (prefers-reduced-motion: reduce){#grit-ptr.is-spin>div{animation:none}}';
  document.head.appendChild(style);
}

function setVisual(damped) {
  const d = Math.min(damped, MAX_PULL);
  const p = Math.max(0, Math.min(1, d / DAMPED_TRIGGER));
  pill.style.transition = 'none';
  pill.style.transform = `translate(-50%, ${-160 + p * 160}%)`;
  pill.style.opacity = String(p);
  ring.style.setProperty('--deg', `${Math.round(p * 360)}deg`);
}
function snapBack() {
  if (!pill) return;
  pill.style.transition = 'transform .2s ease, opacity .2s ease';
  pill.style.transform = 'translate(-50%,-160%)';
  pill.style.opacity = '0';
  pill.classList.remove('is-spin');
}
function fire() {
  refreshing = true;
  pill.style.transition = 'transform .2s ease, opacity .2s ease';
  pill.style.transform = 'translate(-50%,0%)';
  pill.style.opacity = '1';
  pill.classList.add('is-spin');
  setTimeout(() => location.reload(), 180);
}
function reset() { tracking = false; armed = false; }

function onStart(e) {
  if (refreshing) return;
  reset();
  if (e.touches.length !== 1) return;
  if (now() - lastScroll < RECENT_SCROLL_MS) return; // momentum still settling
  if (!atTop() || overlayOpen() || inBlockedTarget(e.target)) return;
  tracking = true;
  startY = e.touches[0].clientY;
  startX = e.touches[0].clientX;
}
function onMove(e) {
  if (!tracking || refreshing) return;
  if (e.touches.length !== 1) { tracking = false; snapBack(); return; }
  const dy = e.touches[0].clientY - startY;
  const dx = e.touches[0].clientX - startX;
  if (!armed) {
    if (Math.abs(dy) < ARM_THRESHOLD && Math.abs(dx) < ARM_THRESHOLD) return;
    if (dy <= 0 || Math.abs(dx) > Math.abs(dy) || !atTop() || overlayOpen()) { tracking = false; return; }
    armed = true;
  }
  if (!atTop() || overlayOpen()) { tracking = false; snapBack(); return; }
  if (dy <= 0) { setVisual(0); return; }
  e.preventDefault();
  setVisual(dy * DAMP);
}
function onEnd() {
  if (!tracking || refreshing) { reset(); return; }
  const deg = parseInt(ring.style.getPropertyValue('--deg'), 10) || 0;
  if (armed && deg >= 360) {
    // Don't discard a half-typed field (Settings saves on blur). Flush focus and
    // abort this refresh; the blur fires its save, the user can pull again.
    const ae = document.activeElement;
    if (ae && ae.matches && ae.matches('input,textarea,select,[contenteditable]')) { ae.blur(); snapBack(); reset(); return; }
    fire();
  } else {
    snapBack();
  }
  reset();
}

export function initPullToRefresh() {
  if (pill) return;
  buildPill();
  window.addEventListener('scroll', () => { lastScroll = now(); }, { passive: true });
  document.addEventListener('touchstart', onStart, { passive: true });
  document.addEventListener('touchmove', onMove, { passive: false });
  document.addEventListener('touchend', onEnd, { passive: true });
  document.addEventListener('touchcancel', () => { snapBack(); reset(); }, { passive: true });
}
